import { parse, visit } from 'graphql';

// Some Staff queries write data. Only these page reads are permitted in the local exploration.
const fields = new Set([
  'getUserPreferences',
  'getOffer',
  'getAccount',
  'getPolicyFromOfferId',
  'getTickets',
  'getFrontContactURL',
  'getAccountClaims',
  'getUnsignedApplications',
  'getDocs',
  'getOnboardingTasks',
  'getInternalTasks',
  'getMemberInventoryImages',
  'getPreBindUWRejectionsByWebUserId',
  'getDriverSuggestions',
  'getCarSuggestions',
  'getMostRecentStaffEditedOffer',
  'getOfferDifference',
  'getOfferInternalNotes',
  'getPolicyInternalNotes',
  'getInternalNotes',
  'getLatestSystemStatus',
  'getAccountBranchPlusPolicies',
  'getUnsignedBixConversions',
  'getAccountPledges',
  'getAccountCommunity',
  'getWriteOffTransactions',
  'getHoldCards',
  'getInspectionStatus',
  'getViolationsForRenewal',
  'getPolicyPaidAndPendingCash',
  'getPolicyPreview'
]);
export const READ_ONLY_MESSAGE =
  'Staff is read-only while the Contextual tools panel POC is on. Turn it off on the POCs page to make changes.';

// Billing's getter reconciles a financial Redis cache. Omit it rather than invoking that path.
export function preparePreviewQuery(document) {
  const omitted = [];
  const query = visit(document, {
    OperationDefinition(node) {
      return {
        ...node,
        selectionSet: {
          ...node.selectionSet,
          selections: node.selectionSet.selections.filter((selection) => {
            if (selection.kind === 'Field' && selection.name.value === 'getBillingDetails') {
              omitted.push(selection.alias?.value || selection.name.value);
              return false;
            }
            return true;
          })
        }
      };
    }
  });
  return { query, omitted };
}
export function isReadOnlyStaffQuery(document) {
  try {
    const ast = typeof document === 'string' ? parse(document) : document;
    const operations = ast.definitions.filter((definition) => definition.kind === 'OperationDefinition');
    return (
      operations.length === 1 &&
      operations[0].operation === 'query' &&
      operations[0].selectionSet.selections.length > 0 &&
      operations[0].selectionSet.selections.every(
        (selection) => selection.kind === 'Field' && fields.has(selection.name.value)
      )
    );
  } catch {
    return false;
  }
}
export function isPreviewRequestAllowed({ url, method = 'GET', body, target = '' }) {
  const parsed = new URL(url, window.location.origin);
  const verb = method.toUpperCase();
  if (/^\/graphql\/?$/.test(parsed.pathname)) {
    try {
      const query = verb === 'GET' ? parsed.searchParams.get('query') : JSON.parse(body).query;
      return isReadOnlyStaffQuery(query);
    } catch {
      return false;
    }
  }
  if (verb === 'GET' || verb === 'HEAD') {
    return true;
  }
  if (verb !== 'POST') {
    return false;
  }
  if (/^cognito-idp\.[\w-]+\.amazonaws\.com$/.test(parsed.hostname)) {
    return /\.(InitiateAuth|GetTokensFromRefreshToken|RespondToAuthChallenge|GetUser|RevokeToken)$/.test(target);
  }
  if (/^cognito-identity\.[\w-]+\.amazonaws\.com$/.test(parsed.hostname)) {
    return /\.(GetId|GetCredentialsForIdentity)$/.test(target);
  }
  // Meilisearch searches use POST without modifying an index.
  return /^\/indexes\/[^/]+\/search$/.test(parsed.pathname) || parsed.pathname === '/multi-search';
}
export function installPreviewReadOnlyNetwork() {
  navigator.sendBeacon = () => false;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const request = new Request(input, init);
    const body = ['GET', 'HEAD'].includes(request.method) ? undefined : await request.clone().text();
    if (
      !isPreviewRequestAllowed({
        url: request.url,
        method: request.method,
        body,
        target: request.headers.get('x-amz-target') || ''
      })
    ) {
      throw new Error(READ_ONLY_MESSAGE);
    }
    return originalFetch(request);
  };
  const open = XMLHttpRequest.prototype.open;
  const send = XMLHttpRequest.prototype.send;
  const setHeader = XMLHttpRequest.prototype.setRequestHeader;
  const requests = new WeakMap();
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    requests.set(this, { method, url, target: '' });
    return open.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
    if (name.toLowerCase() === 'x-amz-target' && requests.has(this)) {
      requests.get(this).target = value;
    }
    return setHeader.call(this, name, value);
  };
  XMLHttpRequest.prototype.send = function (body) {
    if (!isPreviewRequestAllowed({ ...requests.get(this), body })) {
      throw new Error(READ_ONLY_MESSAGE);
    }
    return send.call(this, body);
  };
}
